import { FC, useState, useEffect, useCallback } from 'react'
import {
  Container,
  Title,
  Table
} from './styles'
import { FlexDiv, OrangeButton, OrangeButtonSimpleText } from '../index'
import CircleActionButton from '../../../basic/CircleActionButton'
import {
  AppStatusFilterOptions,
  ApplicationListData,
  RenewalsListData
} from 'src/dtos/portal'
import { format } from 'date-fns'
import { FiSend } from 'react-icons/fi'
import { MdClose, MdDeleteOutline, MdOutlineDownload, MdRemoveRedEye } from 'react-icons/md'
import { TbMailForward, TbMail } from 'react-icons/tb'
import { api } from 'src/services/apiClient'
import Modal from 'react-modal'
import uploadSelector from 'src/components/basic/HTMLUploader';

interface DrawerHeaderProps {
  applicationStatus?: AppStatusFilterOptions;
  applicationData?: Partial<ApplicationListData>;
  renewalData?: Partial<RenewalsListData>;
  submitApplication: () => Promise<void>;
  sendReminder: (appIds: number[]) => Promise<void>;
  rejectApplication: (appIds: number[]) => Promise<void>;
  sendMagicLink: (appIds: number[]) => Promise<void>;
  deleteApplication: (appIds: number[]) => Promise<void>;
  isNewBusiness: boolean;
  submitHidden: boolean;
  applicationLoaded: boolean;
  downloadApplication?: any;
}

const DrawerHeader: FC<DrawerHeaderProps> = ({ 
  applicationStatus, 
  applicationData, 
  submitApplication,
  sendReminder,
  rejectApplication,
  sendMagicLink,
  deleteApplication,
  isNewBusiness,
  renewalData,
  submitHidden,
  applicationLoaded,
  downloadApplication
}) => {
  const [mainButtonDisabled, setMainButtonDisabled] = useState(false)
  const [rejectAppModalOpen, setRejectAppModalOpen] = useState(false)
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false)

  const submitReviewSummary = useCallback(async() => {
    var firmName = isNewBusiness ? applicationData?.firmName : renewalData?.firmName;
    var applicationType = isNewBusiness? "newapplication" : "renewalapplication";
    var uploadResponse = await uploadSelector(firmName + "-" + applicationType + "-review-summary.html");
    if (uploadResponse) {
      var file = uploadResponse[0];
      if(isNewBusiness && applicationData?.id) {
        await api.post(`/Applications/AddReviewSummary?applicationId=${applicationData?.id}&applicationType=1`, file);
      } else if(!isNewBusiness && renewalData?.id) {
        await api.post(`/Applications/AddReviewSummary?applicationId=${renewalData?.id}&applicationType=2`, file);
      }
    }
  }, [applicationData?.id, isNewBusiness, renewalData?.id])

  const handleSubmitApplication = useCallback(async () => {
    setMainButtonDisabled(true)
    await submitReviewSummary()
    await submitApplication()
    setMainButtonDisabled(false)
  }, [submitApplication, submitReviewSummary])

  useEffect(() => {
    if(submitHidden === true && applicationLoaded === true) {
      handleSubmitApplication()
    }
  }, [applicationLoaded, handleSubmitApplication, submitHidden])

  const handleSendReminder = async () => {
    setMainButtonDisabled(true)
    if(isNewBusiness && applicationData?.id) {
      await sendReminder([applicationData.id])
    } else if(!isNewBusiness && renewalData?.id) {
      await sendReminder([renewalData.id])
    }
    setMainButtonDisabled(false)
  }

  const handleRejectApplication = async () => {
    setMainButtonDisabled(true)
    if(isNewBusiness && applicationData?.id) {
      await rejectApplication([applicationData.id])
    } else if(!isNewBusiness && renewalData?.id) {
      await rejectApplication([renewalData.id])
    }
    setMainButtonDisabled(false)
    setRejectAppModalOpen(false)
  }

  const handleSendMagicLinkforApplication = async () => {
    setMainButtonDisabled(true)
    if(isNewBusiness && applicationData?.id) {
      await sendMagicLink([applicationData.id])
    } else if(!isNewBusiness && renewalData?.id) {
      await sendMagicLink([renewalData.id])
    }
    setMainButtonDisabled(false)
    setImpersonateModalOpen(false)
  }

  const handleDeleteApplication = async () => {
    setMainButtonDisabled(true)
    if(isNewBusiness && applicationData?.id) {
      await deleteApplication([applicationData.id])
    } else if(!isNewBusiness && renewalData?.id) {
      await deleteApplication([renewalData.id])
    }
    setMainButtonDisabled(false)
  }

  const handleApplicationDownload = async () => {
    downloadApplication();
  }
  
  return (
    <Container id="app-list-drawer-header">
      <header>
        <Title>Application for {isNewBusiness ? applicationData?.firmName : renewalData?.firmName}</Title>
        {applicationStatus === 'IN_PROGRESS' ? (
          <OrangeButton disabled={mainButtonDisabled} onClick={handleSendReminder} wide>
            <FiSend/>
            Send Reminder
          </OrangeButton>
          ) : null}
        {applicationStatus === 'COMPLETED' ? (
          <OrangeButton disabled={mainButtonDisabled} onClick={handleSubmitApplication} wide>
            <TbMailForward/>
            Submit Application
          </OrangeButton>
        ) : null}
        {applicationStatus === 'EXPIRING_POLICIES' ? (
          <a href={`mailto:${isNewBusiness ? applicationData?.applicantEmail : renewalData?.userEmail}`} style={{ textDecoration: 'none' }}>
            <OrangeButton disabled={mainButtonDisabled} onClick={e => e.stopPropagation()} wide>
              <TbMail/>
              Contact
            </OrangeButton>
          </a>
        ) : null}
      </header>
      <main>
        <Table>
          <thead>
            {!applicationStatus ? (
              <tr>
                <th>Date Completed</th>
                <th>Date Submitted</th>
              </tr>
            ) : applicationStatus !== 'EXPIRING_POLICIES' ? (
              <tr>
                <th>Date Started</th>
                {applicationStatus === 'IN_PROGRESS' ? (
                  <>
                    <th>Last Updated</th>
                    <th>Progress</th>
                  </>
                ) : null}
                {applicationStatus !== 'IN_PROGRESS' ? (
                  <th>Date Completed</th>
                ) : null}
              </tr>
            ) : (
              <tr>
                <th>Effective Date</th>
                <th>Expiry Date</th>
              </tr>
            )}
          </thead>
          <tbody>
            {!applicationStatus ? (
              <tr>
                <td>{isNewBusiness ? (applicationData?.dateCompleted ?? '') : (renewalData?.dateCompleted ?? '')}</td>
                <td>{isNewBusiness ? (applicationData?.dateSubmitted ?? '') : (renewalData?.dateSubmitted ?? '')}</td>
              </tr>
            ) : applicationStatus !== 'EXPIRING_POLICIES' ? (
              <tr>
                {isNewBusiness ? (
                  <td>{applicationData?.dateStarted ? format(new Date(applicationData.dateStarted), 'MM/dd/yyyy') : ''}</td>
                ) : (
                  <td>{renewalData?.dateStarted ? format(new Date(renewalData.dateStarted), 'MM/dd/yyyy') : ''}</td>
                )}
                {applicationStatus === 'IN_PROGRESS' ? (
                  <>
                  {isNewBusiness ? (
                    <>
                      <td>{applicationData?.dateStarted ? format(new Date(applicationData.dateStarted), 'MM/dd/yyyy') : ''}</td>
                      <td>{applicationData?.percentageOfCompletion}%</td>
                    </>
                  ) : (
                    <>
                      <td>{renewalData?.dateStarted ? format(new Date(renewalData.dateStarted), 'MM/dd/yyyy') : ''}</td>
                      <td>{renewalData?.percentageOfCompletion}%</td>
                    </>
                  )}
                  </>
                ) : null}
                {applicationStatus !== 'IN_PROGRESS' ? 
                isNewBusiness ? (
                  <td>{applicationData?.dateCompleted ? format(new Date(applicationData.dateCompleted), 'MM/dd/yyyy') : ''}</td>
                ) : (
                  <td>{renewalData?.dateCompleted ? format(new Date(renewalData.dateCompleted), 'MM/dd/yyyy') : ''}</td>
                ) : null}
              </tr>
            ) : (
              <tr>
                <td>
                  {
                    isNewBusiness ? 
                    (!!applicationData?.dateStarted ? format(new Date(applicationData.dateStarted), 'MM/dd/yyyy') : '') : 
                    (!!renewalData?.dateStarted ? format(new Date(renewalData.dateStarted), 'MM/dd/yyyy') : '')
                  }
                </td>
                <td>
                  {
                    isNewBusiness ? 
                    (!!applicationData?.dateRejected ? format(new Date(applicationData.dateRejected), 'MM/dd/yyyy') : '') : 
                    (!!renewalData?.dateRejected ? format(new Date(renewalData.dateRejected), 'MM/dd/yyyy') : '')
                  }
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <FlexDiv id="app-list-cta-buttons" maxWidth='fit-content' gap="48px">
          {applicationStatus !== 'IN_PROGRESS' && applicationStatus !== 'EXPIRING_POLICIES' ? (
            <>
              <CircleActionButton
                label="Download"
                bg="#EFEFEF"
                onClick={handleApplicationDownload}
              >
                <MdOutlineDownload/>
              </CircleActionButton>
              <a href={`mailto:${isNewBusiness ? applicationData?.applicantEmail : renewalData?.userEmail}`} style={{ textDecoration: 'none' }}>
                <CircleActionButton
                  label="Contact"
                  bg="#EFEFEF"
                >
                  <TbMail/>
                </CircleActionButton>
              </a>
            </>
          ) : null}
          {applicationStatus === 'REJECTED' ? (
            <CircleActionButton
              label="Delete"
              bg="#EFEFEF"
              onClick={handleDeleteApplication}
            >
              <MdDeleteOutline/>
            </CircleActionButton>
          ) : null}
          {applicationStatus !== undefined && applicationStatus !== 'REJECTED' && applicationStatus !== 'EXPIRING_POLICIES' ? (
            <CircleActionButton
              label="Reject"
              bg="#EFEFEF"
              onClick={() => setRejectAppModalOpen(true)}
            >
              <MdClose/>
            </CircleActionButton>
          ) : null}
          {applicationStatus !== undefined && applicationStatus === 'IN_PROGRESS' ? (
            <CircleActionButton
              label="Impersonate"
              bg="#EFEFEF"
              onClick={() => setImpersonateModalOpen(true)}
            >
              <MdRemoveRedEye/>
            </CircleActionButton>
          ) : null}
        </FlexDiv>
      </main>
      <Modal
        isOpen={rejectAppModalOpen}
        onRequestClose={(e) => {
          e.stopPropagation()
          setRejectAppModalOpen(false)
        }}
        overlayClassName="react-modal-overlay"
        className="react-modal-content"
      >
        <FlexDiv dir='column' gap="16px">
          <strong>Are you sure you want to reject the selected application?</strong>
          <span>Once an application is rejected, you will not be able to submit it to ISI.</span>
          <FlexDiv dir="column" gap="24px" margin="48px 0 0 0" align='flex-end'>
            <OrangeButton
              onClick={handleRejectApplication}
              disabled={mainButtonDisabled}
            >
              Reject application
            </OrangeButton>
            <OrangeButtonSimpleText
              onClick={(e) => {
                e.stopPropagation()
                setRejectAppModalOpen(false)
              }}
              disabled={mainButtonDisabled}
            >
              Cancel
            </OrangeButtonSimpleText>
          </FlexDiv>
        </FlexDiv>
      </Modal>
      {/* send magic link */}
      <Modal
        isOpen={impersonateModalOpen}
        onRequestClose={(e) => {
          e.stopPropagation()
          setImpersonateModalOpen(false)
        }}
        overlayClassName="react-modal-overlay"
        className="react-modal-content"
      >
        <FlexDiv dir='column' gap="16px">
          <strong>Are you sure you want to impersonate the user of the selected application?</strong>
          <FlexDiv dir="column" gap="24px" margin="48px 0 0 0" align='flex-end'>
            <OrangeButton
              onClick={handleSendMagicLinkforApplication}
              disabled={mainButtonDisabled}
            >
              Impersonate
            </OrangeButton>
            <OrangeButtonSimpleText
              onClick={(e) => {
                e.stopPropagation()
                setImpersonateModalOpen(false)
              }}
              disabled={mainButtonDisabled}
            >
              Cancel
            </OrangeButtonSimpleText>
          </FlexDiv>
        </FlexDiv>
      </Modal>
    </Container>
  )
}

export default DrawerHeader